import { useMutation, useQuery } from "@tanstack/react-query";
import { EntityType } from "../types/api/logs";
import { TopicResponse, PostFullResponse } from "../types/api/forum";
import { ComplaintResponse } from "../types/api/complaints";
import { CommentResponse } from "../types/api/comments";
import { ReviewResponse } from "../types/api/review";
import { fetchUser } from "../services/users";
import { fetchAds } from "../services/ads";
import { fetchReview } from "../services/reviews";
import { deleteComment, fetchComment } from "../services/comments";
import { fetchPost } from "../services/forum";
import { fetchTopic } from "../services/forum";

import { blockAd } from "../services/ads";
import { blockUsers } from "../services/users";
import { deleteReview } from "../services/reviews";
import { deletePosts, deleteTopics } from "../services/forum";

const getEntity = (entityType: EntityType, entityId: string): Promise<any> => {
  switch (entityType) {
    case "ad":
      return fetchAds(entityId).then((data) => data);
    case "user":
    case "profile":
    case "seller":
      return fetchUser(entityId).then((data) => data);
    case "comment":
    case "chat":
      return fetchComment(entityId).then((data) => data);
    case "review":
      return fetchReview(entityId).then((data) => data);
    case "post":
      return fetchPost(entityId).then((data) => data);
    case "topic":
      return fetchTopic(entityId).then((data) => data);
    case "settings":
    case "system":
    case "moderator":
    case undefined:
      return Promise.resolve(null);
    default:
      return Promise.resolve(null);
  }
};

export const useEntityQuery = (entityType: EntityType, entityId: string) => {
  return useQuery({
    queryKey: ["entity", entityType, entityId],
    queryFn: () => getEntity(entityType, entityId),
    enabled: !!entityId,
    retry: false,
  });
};

const deleteEntity = (complaint: ComplaintResponse): Promise<any> => {
  const id = complaint.targetEntityId;
  switch (complaint.targetEntityType) {
    case "ad":
      return blockAd([id]).then((data) => data);
    case "user":
    case "profile":
    case "seller":
      return blockUsers({ userIds: [id] }).then((data) => data);
    case "comment":
      return deleteComment([id]).then((data) => data);
    case "review":
      return deleteReview(id).then((data) => data);
    case "post":
      return deletePosts({ ids: [id] }).then((data) => data);
    case "topic":
      return deleteTopics({ ids: [id] }).then((data) => data);
    case "settings":
    case "system":
      return Promise.resolve(null);
    default:
      return Promise.resolve(null);
  }
};

export const useDeleteEntitiesMutation = () => {
  return useMutation({
    mutationFn: async (complaints: ComplaintResponse[]) => {
      const deletePromises = complaints.map((complaint) =>
        deleteEntity(complaint)
      );
      const results = await Promise.allSettled(deletePromises);

      const successful = results.filter(
        (result) => result.status === "fulfilled"
      ).length;
      const failed = results.filter(
        (result) => result.status === "rejected"
      ).length;

      return {
        successful,
        failed,
        total: complaints.length,
        results,
      };
    },
  });
};

export const getUrlForOpenEntity = async (
  entityType: EntityType,
  entityId: string
): Promise<string | undefined> => {
  const data = await getEntity(entityType, entityId);

  switch (entityType) {
    case "ad":
      return `/ads/ad?id=${entityId}`;
    case "user":
    case "profile":
    case "seller":
      return `/users/user?id=${entityId}`;
    case "moderator":
      return `/moderators`;
    case "comment":
      return (data as CommentResponse)
        ? `/ads/faq?id=${data.entityId}`
        : undefined;
    case "chat":
      return `/users/user?id=${entityId}`;
    case "review":
      return (data as ReviewResponse) ? `/ads/ad?id=${data.ad.id}` : undefined;
    case "post":
      const post = data as PostFullResponse;
      if (post) {
        const topic = await fetchTopic(post.topicId).then((data) => data);
        return (data as PostFullResponse)
          ? `/forums/forumComments/?topicID=${topic.id}&parentCategoryID=${topic.categoryId}&subcategoryID=${topic.catalogId}`
          : undefined;
      }
      return undefined;
    case "topic":
      return (data as TopicResponse)
        ? `/forums/forumComments/?topicID=${data.id}&parentCategoryID=${data.categoryId}6&subcategoryID=${data.catalogId}`
        : undefined;
    case "settings":
    case "system":
      return undefined;
    default:
      return undefined;
  }
};
